import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { collection, doc, Timestamp } from 'firebase/firestore';
import { FormProvider, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { setGameEventData } from 'services';
import { strings } from 'strings';
import { useForm } from 'hooks';

import { UploadGameEventImage } from '../UploadGameEventImage';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../../../context/GlobalModal';
import { Box, ErrorMessage, FlexContainer, Text } from '../../../../../../shared/Typhography';
import { Button, Input, SelectForm } from '../../../../../../shared/Components';
import { getUTCDate, isSameDay } from '../../../../../../utilities/date';
import { firestore, STORAGE_URL, uploadGameEventImage } from '../../../../../../services';
import { GameEventLevelField } from '../GameEventLevelForm/validations/GameEventLevelFormSchema';
import GameEventLevelForm from '../GameEventLevelForm/GameEventLevelForm';

import DaterangePicker from './components/DaterangePicker';
import { GameEventField, gameEventFormSchema } from './validations/GameEventFormSchema';

const FormContainer = styled.form`
  margin-top: 50px;
`;

const LevelButtonContainer = styled(FlexContainer)`
  position: relative;
`;

const GAME_EVENT_DIFFICULTY = [
  {
    label: '1',
    value: 1,
  },
  {
    label: '2',
    value: 2,
  },
  {
    label: '3',
    value: 3,
  },
  {
    label: '4',
    value: 4,
  },
  {
    label: '5',
    value: 5,
  },
];

interface GameEventFormProps {
  title: string;
  value?: GameEventField;
}

const GameEventForm = ({ title, value }: GameEventFormProps) => {
  const [gameEventImage, setGameEventImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const history = useHistory();
  const { showModal } = useGlobalModalContext();

  const { control, formState, trigger, setValue, handleSubmit, watch, ...methods } = useForm({
    schema: gameEventFormSchema,
    defaultValues: value,
  });

  const { image, startDate } = watch();
  const onImageChange = useCallback(
    (file: File) => {
      const url = file ? URL.createObjectURL(file) : '';

      if (image) {
        URL.revokeObjectURL(image);
      }

      setGameEventImage(file);
      setValue('image', url);
    },
    [image],
  );

  const uploadFile = useCallback(async (id: string, file: File): Promise<string> => {
    const result = await uploadGameEventImage(id, file);

    if (!result) {
      toast.error(strings.gameEvent.create.toastError);

      throw 'Failed to upload game event image';
    }

    return result.ref.fullPath;
  }, []);

  const submit = useCallback(
    (published: boolean) => {
      const proceed = handleSubmit(async (data) => {
        if (isSaving) {
          return;
        }

        setIsSaving(true);

        try {
          const gameEventsRef = collection(firestore, 'gameEvents');
          const gameEventId = data.id || doc(gameEventsRef).id;

          const imagePath = gameEventImage
            ? await uploadFile(gameEventId, gameEventImage)
            : data.image;

          const payload = {
            id: gameEventId,
            name: data.name,
            published,
            image: gameEventImage
              ? `${STORAGE_URL}/${imagePath}?timestamp=${new Date().getTime()}`
              : imagePath,
            entryFee: {
              coins: data.entryFee,
            },
            startDate: getUTCDate(data.startDate),
            endDate: getUTCDate(data.endDate),
            totalReward: {
              coins: data.levels.reduce(
                (sum: number, level: GameEventLevelField) => sum + level.reward,
                0,
              ),
            },
            levels: data.levels.map((level: GameEventLevelField, index: number) => ({
              ...(level.id ? { id: level.id } : {}),
              difficulty: level.difficulty.value,
              level: index + 1,
              reward: {
                coins: level.reward,
              },
              puzzles: level.puzzles,
            })),
            difficulty: data.difficulty.value,
            updatedAt: Timestamp.now(),
            createdAt: Timestamp.now(),
          };

          await setGameEventData(payload);

          toast.success(data.id ? strings.gameEvent.edit.toast : strings.gameEvent.create.toast);

          setGameEventImage(null);

          history.push(published ? `/game-events` : `/edit-game-event/${gameEventId}`);
        } catch (error) {
          toast.error(
            data.id
              ? strings.gameEvent.edit.toastErrorSave
              : strings.gameEvent.create.toastErrorSave,
          );
        } finally {
          setIsSaving(false);
        }
      });

      if (published && startDate && isSameDay(new Date(), startDate)) {
        return showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
          content: () => (
            <div style={{ margin: '50px' }}>
              <Text textAlign="center">{strings.sameDayCreateGameEventConfirm}</Text>
            </div>
          ),
          action: () => {
            proceed();
            return true;
          },
          confirmText: strings.gameEvent.form.buttons.ok,
          disableNotifications: true,
        });
      }

      proceed();
    },
    [gameEventImage, setIsSaving, isSaving, startDate, showModal],
  );

  const errors = formState.errors;

  const {
    fields: levels,
    append,
    remove,
  } = useFieldArray({
    name: 'levels',
    control,
  });

  const onAddNewLevel = useCallback(() => {
    append({
      level: levels.length + 1,
      puzzles: [],
    });
    trigger('levels');
  }, [append, levels]);

  const onRemoveLevel = useCallback(
    (index: number) => {
      remove(index);
      trigger('levels');
    },
    [append, levels],
  );

  const onDateRangeChange = useCallback((dates) => {
    if (dates[0]) {
      const startDate = dates[0];

      setValue('startDate', startDate);
    }

    if (dates[1]) {
      const endDate = dates[1];

      setValue('endDate', endDate);
    }
  }, []);

  const shouldShowAddLevelButton = useMemo(() => {
    const lastEventState = formState.errors.levels?.[levels.length - 1];

    return levels.length === 0 || !lastEventState;
  }, [levels, formState]);

  const { message: levelErrorMessage } = (errors['levels'] as any) || {};
  const { message: imageErrorMessage } = (errors['image'] as any) || {};

  const dataRangeError = errors['startDate']?.message ? errors['startDate'] : errors['endDate'];
  return (
    <FormProvider
      control={control}
      formState={formState}
      setValue={setValue}
      trigger={trigger}
      handleSubmit={handleSubmit}
      watch={watch}
      {...methods}
    >
      <Box margin="150px auto 20px auto">
        <Text size={2} weight="bold">
          {title}
        </Text>
        <FormContainer>
          <FlexContainer flexDirection="column">
            <FlexContainer
              flexWrap="wrap"
              justifyContent="space-between"
              padding="30px"
              flexDirection="column"
              flex="1"
            >
              <UploadGameEventImage onFileChange={onImageChange} />
              {imageErrorMessage && (
                <ErrorMessage style={{ position: 'relative', left: 0, bottom: 0 }}>
                  {imageErrorMessage}
                </ErrorMessage>
              )}
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-between">
              <Input
                required
                placeholder={strings.gameEvent.form.placeholder.name}
                name="name"
                type="text"
                width="400px"
                control={control}
                error={errors['name']}
                marginBottom={20}
                marginRight={30}
                margin="0 auto 40px"
                disabled={isSaving}
                defaultValue={value?.name || ''}
              />
              <div style={{ width: '400px', margin: '0 auto 40px' }}>
                <DaterangePicker
                  labelStartDate={strings.gameEvent.form.placeholder.startDate}
                  labelEndDate={strings.gameEvent.form.placeholder.endDate}
                  startDate={value?.startDate || null}
                  endDate={value?.endDate || null}
                  selectsRange={true}
                  inline={true}
                  disabled={isSaving}
                  onChange={onDateRangeChange}
                  error={dataRangeError}
                />
              </div>
            </FlexContainer>

            <FlexContainer flexWrap="wrap" justifyContent="space-between">
              <Input
                required
                placeholder={strings.gameEvent.form.placeholder.enterFee}
                name="entryFee"
                type="number"
                width="400px"
                control={control}
                disabled={isSaving}
                error={errors['entryFee']}
                marginBottom={20}
                marginRight={30}
                margin="0 auto 40px"
                defaultValue={value?.entryFee || 0}
              />
              <div style={{ width: '400px', margin: '0 auto 40px' }}>
                <SelectForm
                  required
                  placeholder={strings.gameEvent.form.placeholder.difficulty}
                  name="difficulty"
                  control={control}
                  disabled={isSaving}
                  values={GAME_EVENT_DIFFICULTY}
                  error={errors['difficulty']}
                  defaultValue={value?.difficulty}
                  width="400px"
                />
              </div>
            </FlexContainer>
          </FlexContainer>
        </FormContainer>
      </Box>

      {levels.map((level, index) => (
        <Box margin="0 auto 20px auto" key={level.id}>
          <GameEventLevelForm
            index={index}
            level={index + 1}
            isLast={levels.length === index + 1}
            onRemove={onRemoveLevel}
            disabled={isSaving}
          />
        </Box>
      ))}

      {shouldShowAddLevelButton && (
        <Box margin="20px auto">
          <LevelButtonContainer>
            <Button
              style={{ minWidth: 300, margin: '0 auto' }}
              variant="primary"
              height={40}
              disabled={isSaving}
              onClick={onAddNewLevel}
            >
              {strings.gameEvent.form.buttons.addLevel}
            </Button>
            {levelErrorMessage && <ErrorMessage>{levelErrorMessage}</ErrorMessage>}
          </LevelButtonContainer>
        </Box>
      )}

      <Box margin="0 auto 20px auto">
        <FlexContainer
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
          margin="40px 0 0 0"
        >
          <Button
            style={{ minWidth: 200 }}
            variant="danger"
            disabled={isSaving}
            height={40}
            onClick={() => history.push('/game-events')}
          >
            {strings.gameEvent.form.buttons.cancel}
          </Button>
          <Button
            style={{ minWidth: 200 }}
            variant="primary"
            disabled={isSaving}
            height={40}
            onClick={() => submit(false)}
          >
            {strings.gameEvent.form.buttons.saveDraft}
          </Button>
          <Button
            style={{ minWidth: 200 }}
            variant="primary"
            disabled={isSaving}
            height={40}
            onClick={() => submit(true)}
          >
            {strings.gameEvent.form.buttons.publish}
          </Button>
        </FlexContainer>
      </Box>
    </FormProvider>
  );
};

export default GameEventForm;
